import React, { useEffect, useRef, useState } from 'react';
import { AlertTriangle } from 'lucide-react';
import { merchantApi, type OrderCommissionView } from '../../services/merchantApi';
import { useDataVersion } from '../../services/dataChangeBus';
import { RubyLoader } from '../../components/RubyLoader';

/**
 * PARTNER COMMISSION, on the order that produced it.
 *
 * WHY THIS EXISTS. An order used to show nothing at all about commission, so
 * "no partner was involved" and "the commission engine dropped this" looked
 * exactly the same. This panel always renders — the empty state is a STATEMENT
 * ("No partner"), never a hidden section.
 *
 * NOTHING HERE IS RECOMPUTED. Every amount is a ledger row billing-service
 * wrote, read back as stored. The one computed thing is `wouldApplyPlan`, and
 * it is a RATE shown only when nothing was earned — labelled "would have paid"
 * and never as a figure anyone is owed.
 */

const money = (cents: number) => {
  const neg = cents < 0;
  const v = Math.abs(cents) / 100;
  return `${neg ? '−' : ''}$${v.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
};

const when = (ts?: number) =>
  ts ? new Date(ts).toLocaleDateString(undefined, { day: 'numeric', month: 'short', year: 'numeric' }) : '—';

/** The rule, in the words the plans editor uses. */
const rateLabel = (r?: { basis: string; rate: number }) => {
  if (!r) return 'no rule';
  if (r.basis === 'flat') return money(r.rate);
  const of = r.basis === 'percent_of_margin' ? 'of margin' : 'of price';
  return `${r.rate}% ${of}`;
};

const STATUS_CHIP: Record<string, string> = {
  pending: 'bg-stone-100 text-stone-700',
  clearing: 'bg-amber-50 text-amber-800',
  cleared: 'bg-emerald-50 text-emerald-700',
  paid: 'bg-emerald-600 text-white',
  forfeited: 'bg-rose-50 text-rose-700',
};

const Field: React.FC<{ label: string; children: React.ReactNode; sub?: React.ReactNode }> = ({ label, children, sub }) => (
  <div className="border-b border-stone-200 px-5 py-3.5 sm:border-b-0 sm:border-r last:border-0">
    <dt className="text-[11px] font-semibold uppercase tracking-[0.08em] text-stone-400">{label}</dt>
    <dd className="mt-1 text-[14px] font-semibold text-stone-900">
      {children}
      {sub && <span className="block text-[12px] font-normal text-stone-400">{sub}</span>}
    </dd>
  </div>
);

const CommissionPanel: React.FC<{ orderId: string }> = ({ orderId }) => {
  const [data, setData] = useState<OrderCommissionView | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const dataVersion = useDataVersion();
  /** The order the spinner was last shown for — a re-read of the same one is silent. */
  const shownFor = useRef<string | null>(null);

  useEffect(() => {
    let alive = true;
    if (shownFor.current !== orderId) { shownFor.current = orderId; setLoading(true); }
    setError(null);
    merchantApi.orderCommission(orderId)
      .then((r) => { if (alive) setData(r.commission); })
      // A commission read must never take the order screen down with it.
      .catch((e) => { if (alive) setError(e?.message || 'Could not load commission'); })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  // dataVersion: re-read in place after a save anywhere on the order screen.
  }, [orderId, dataVersion]);

  const earned = !!data && data.events.length > 0;
  const partner = data?.attributedPartner;

  return (
    <section className="overflow-hidden rounded-2xl border border-stone-200 bg-white">
      <header className="flex flex-wrap items-center justify-between gap-3 border-b border-stone-200 px-5 py-4">
        <div className="min-w-0">
          <h3 className="text-[15px] font-semibold text-stone-900">Partner commission</h3>
          <p className="text-[12px] text-stone-400">
            {earned && partner
              ? <>{partner.name || partner.id}{partner.referralCode && <> · <span className="font-mono">{partner.referralCode}</span></>}</>
              : 'Read from the commission ledger · nothing recomputed'}
          </p>
        </div>
        {earned && (
          <span className="text-[18px] font-bold tabular-nums text-stone-900">{money(data!.totalCents)}</span>
        )}
        {!loading && !error && data && !earned && (
          <span className="rounded-full bg-stone-100 px-2.5 py-1 text-[11px] font-semibold text-stone-600">
            {data.reason === 'partner_attributed_no_events' ? 'Nothing recorded' : 'No partner'}
          </span>
        )}
      </header>

      {loading && (
        <RubyLoader variant="section" label="Loading commission…" />
      )}

      {!loading && error && (
        <p className="flex items-start gap-1.5 px-5 py-4 text-[13px] text-amber-700">
          <AlertTriangle size={14} className="mt-0.5 shrink-0" /> {error}
        </p>
      )}

      {/* EARNED — the ledger rows, each with the rule that priced it. */}
      {!loading && !error && earned && (
        <div className="overflow-x-auto">
          <table className="w-full text-[13px]">
            <thead>
              <tr className="border-b border-stone-200 text-left text-[11px] uppercase tracking-[0.08em] text-stone-400">
                <th className="px-5 py-2.5 font-semibold">Item</th>
                <th className="px-3 py-2.5 text-right font-semibold">Gross</th>
                <th className="px-3 py-2.5 font-semibold">Rule</th>
                <th className="px-3 py-2.5 text-right font-semibold">Commission</th>
                <th className="px-3 py-2.5 font-semibold">Status</th>
                <th className="px-5 py-2.5 font-semibold">Clears</th>
              </tr>
            </thead>
            <tbody>
              {data!.events.map((e) => (
                <tr key={e.eventId} className="border-b border-stone-100 last:border-0">
                  <td className="px-5 py-3 text-stone-800">{e.sourceLabel || e.sourceType.replace(/_/g, ' ')}</td>
                  <td className="px-3 py-3 text-right tabular-nums text-stone-600">{money(e.grossCents)}</td>
                  <td className="px-3 py-3 text-stone-700">
                    {rateLabel({ basis: e.basisApplied, rate: e.rateApplied })}
                    <span className="ml-1.5 font-mono text-[11px] text-stone-400">{e.planId} v{e.planVersion}</span>
                  </td>
                  <td className="px-3 py-3 text-right font-semibold tabular-nums text-stone-900">{money(e.commissionCents)}</td>
                  <td className="px-3 py-3">
                    <span className={`rounded-full px-2 py-0.5 text-[11px] font-semibold capitalize ${STATUS_CHIP[e.status] || 'bg-stone-100 text-stone-700'}`}>
                      {e.status}
                    </span>
                  </td>
                  <td className="px-5 py-3 tabular-nums text-stone-500">{when(e.clearingDate)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* NO PARTNER — the statement, as facts. */}
      {!loading && !error && data && !earned && (
        <>
          <dl className="m-0 grid grid-cols-1 sm:grid-cols-4">
            <Field
              label="Partner"
              sub={data.reason === 'partner_attributed_no_events' ? 'Attributed, but no ledger row' : 'Signed with the bare deal code'}
            >
              {data.reason === 'partner_attributed_no_events' && partner ? (partner.name || partner.id) : 'None credited'}
            </Field>
            <Field
              label="Deal"
              sub={data.dealVersion
                ? (data.dealVersion.source === 'unversioned'
                  ? 'No published version'
                  : `v${data.dealVersion.versionNo} · ${data.dealVersion.source === 'pinned' ? 'signed' : 'current'}`)
                : undefined}
            >
              {data.dealCode ? <span className="font-mono">{data.dealCode}</span> : '—'}
            </Field>
            <Field
              label="Plan"
              sub={data.wouldApplyPlan ? `v${data.wouldApplyPlan.version}` : undefined}
            >
              {data.wouldApplyPlan ? (data.wouldApplyPlan.name || data.wouldApplyPlan.planId) : '—'}
              {/* A DRAFT plan acting as the default prices real money — say so. */}
              {data.wouldApplyPlan?.status && data.wouldApplyPlan.status !== 'active' && (
                <span className="ml-1.5 rounded bg-amber-100 px-1.5 py-0.5 text-[11px] font-medium text-amber-800">
                  {data.wouldApplyPlan.status}
                </span>
              )}
            </Field>
            <Field label="Commission">
              <span className="tabular-nums">$0.00</span>
            </Field>
          </dl>

          {/* A partner IS attributed but nothing was earned — a defect, said plainly. */}
          {data.reason === 'partner_attributed_no_events' && partner && (
            <p className="flex items-start gap-2 border-t border-amber-200 bg-amber-50 px-5 py-3 text-[13px] text-amber-800">
              <AlertTriangle size={14} className="mt-0.5 shrink-0" />
              <span>
                <b>{partner.name || partner.id}</b> sold this order but has no ledger row — the commission was never
                emitted, or no plan rule matched. Check before payout.
              </span>
            </p>
          )}

          {/* THE NEAR-MISS: partner links on the deal that the customer did not use. */}
          {data.unusedPartnerLinks.length > 0 && (
            <div className="flex flex-wrap items-center gap-2 border-t border-amber-200 bg-amber-50 px-5 py-3 text-[13px] text-amber-800">
              <AlertTriangle size={14} className="shrink-0" />
              <span>Unused partner link{data.unusedPartnerLinks.length === 1 ? '' : 's'} on this deal:</span>
              {data.unusedPartnerLinks.map((l) => (
                <span key={l.code} className="inline-flex items-center gap-1.5">
                  <span className="rounded bg-stone-900 px-1.5 py-0.5 font-mono text-[11px] text-white">{l.code}</span>
                  {l.partnerName && <span className="text-stone-700">{l.partnerName}</span>}
                </span>
              ))}
            </div>
          )}

          {/* A RATE, never money. */}
          {data.wouldApplyPlan && (
            <p className="border-t border-stone-200 bg-stone-50 px-5 py-2.5 text-[12.5px] text-stone-600">
              Had a partner been credited: hardware <b className="text-stone-800">{rateLabel(data.wouldApplyPlan.deviceRate)}</b>
              {' · '}software <b className="text-stone-800">{rateLabel(data.wouldApplyPlan.softwareRate)}</b>
            </p>
          )}
        </>
      )}
    </section>
  );
};

export default CommissionPanel;
